import React, { useState } from 'react'
import { Button, Card, CardContent, Divider, Grid, TextField } from '@mui/material'
import { SearchOutlined } from '@mui/icons-material'
import { Formik } from 'formik'
import { NeedsClusterRemovedDetailsDto, needsClusterRemovedDetailsValidationSchema, VehicleDto } from 'dtos'
import NeedsClusterRemovedStepper from './NeedsClusterRemovedStepper'
import { LabelField, GreenText } from 'components'
import * as controllers from 'controllers'
import { useLayout } from 'context'
import { useParams } from 'react-router-dom'

interface Step1ScanClusterBarCodeProps {
  initialValues: NeedsClusterRemovedDetailsDto
  onNext: (values: NeedsClusterRemovedDetailsDto) => void
  vehicle: VehicleDto
}

export default function Step1ScanClusterBarCode({ initialValues, onNext, vehicle }: Step1ScanClusterBarCodeProps) {
  const layout = useLayout()
  const { vehicleGuid } = useParams()

  const [search, setSearch] = useState<string>('')
  const [searchError, setSearchError] = useState<string>('')
  const [hasSearched, setHasSearched] = useState<boolean>(false)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
  }

  const searchVehicle = (setValues: any) => {
    layout.setIsLayoutLoading(true)
    controllers
      .getNeedsClusterRemovedSearch(search)
      .then(response => {
        if (response.value) {
          const vehicleLast6OfVin = vehicle.vin?.slice(-6)
          if (search === vehicleLast6OfVin || search === vehicle.importerStockNumber) {
            setValues(response.value)
            setHasSearched(true)
            setSearch('')
            setSearchError('')
          } else {
            setValues(new VehicleDto())
            setHasSearched(false)
            setSearchError('Entered VIN does not match the last 6 of the VIN or Importer Stock #')
          }
        } else {
          setValues(new VehicleDto())
          setHasSearched(false)
          setSearchError('No results found')
        }
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => {
        controllers.vehicles
          .startClusterRemoval(vehicleGuid!)
          .then(() => {
            onNext(values)
          })
          .finally(() => {
            formikHelpers.setSubmitting(false)
          })
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={needsClusterRemovedDetailsValidationSchema}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        setTouched,
        values,
        validateForm
      }) => (
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center'>
                <Grid item xs={12}>
                  <NeedsClusterRemovedStepper activeStep={0} />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    error={Boolean(searchError)}
                    helperText={searchError}
                    fullWidth
                    label='Last 6 of VIN or Importer Stock #'
                    onChange={handleSearchChange}
                    value={search}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        searchVehicle(setValues)
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    endIcon={<SearchOutlined />}
                    color='primary'
                    fullWidth
                    onClick={() => searchVehicle(setValues)}
                    variant='contained'
                  >
                    SEARCH
                  </Button>
                </Grid>

                {hasSearched && vehicle && (
                  <>
                    <Grid item xs={12}>
                      <LabelField label='VIN' value={vehicle?.vin} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Year' value={vehicle?.year} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Make' value={vehicle?.make} />
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField label='Model' value={vehicle?.model} />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} marginTop={1}>
                  <Button color='primary' fullWidth type='submit' variant='contained' disabled={!hasSearched || !values.vin}>
                    START CLUSTER REMOVAL
                  </Button>
                </Grid>

                <Grid item xs={12} marginTop={1}>
                  <GreenText variant='outlined'>
                    {`**Vehicle Check-In Steps:**

Displays the current step of the vehicle in the Needs Cluster 
Removed process.

**Last 6 of VIN/Importer Stock Number Search:**

A free-text input field for entering either the last six 
characters of the VIN or the Importer Stock Number of a 
vehicle.

**SEARCH Button:**

Clicking the Search button performs a check to match the 
entered Last 6 of VIN or Importer Stock Number with the 
currently selected vehicle.

If a match is found, the VIN, Year, Make, and Model fields 
will auto-populate with the vehicle’s details.
If no match is found, red text will appear, displaying: “No 
Match Found.”

**VIN:**

A read-only label that displays the VIN of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Year:**

A read-only label that displays the Year of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Make:**

A read-only label that displays the Make of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**Model:**

A read-only label that displays the Model of the searched 
vehicle.

By default, the field displays a “-” if no vehicle has been 
found.

**START CLUSTER REMOVAL Button:**

Clicking this button will:

Save the name of the user who pressed the button along 
with the date and time to the database.

Advance the user to the next step in the Needs Cluster 
Removed process.

This button is only enabled if the search for the Last 6 of 
VIN or Importer Stock Number results in a match.

`}
                  </GreenText>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  )
}
